import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageContainer from '../components/Layout/PageContainer';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Badge from '../components/UI/Badge';
import RiskBadge from '../components/UI/RiskBadge';
import { 
  ArrowLeft, Activity, Shield, Clock, 
  Network, Settings, AlertTriangle, 
  RefreshCw, Database, Cpu, Globe, 
  History, LayoutGrid, Plus, ShieldCheck, 
  Server, Trash2, Lightbulb, CheckCircle2, 
  Info, AlertCircle, Loader2, Edit2,
  ShieldAlert
} from 'lucide-react';
import { apiClient } from '../api';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset] = useState(null);
  const [riskData, setRiskData] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [driftEvents, setDriftEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [iocAlerts, setIocAlerts] = useState([]);
  const [cveAlerts, setCveAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);

  const fetchAssetData = async () => {
    if (!id) {
      setError('No asset ID provided');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      try {
        const assetRes = await apiClient.get(`/api/servers/${id}`);
        if (!assetRes.data || Object.keys(assetRes.data).length === 0) {
          throw new Error('Empty asset data received');
        }

        const server = assetRes.data;
        setAsset({
          id: server.id,
          name: server.hostname,
          ip_address: server.ip_address,
          os_type: server.os_info || server.os_type,
          hostname: server.hostname,
          created_at: server.created_at,
          last_scanned: server.last_seen,
          asset_criticality: 'MEDIUM',
          asset_type: server.os_type || 'server',
          is_baseline: false,
          risk_score: 0,
        });
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.error || err.message;
        setError(`Failed to retrieve asset (${status || 'Network Error'}): ${detail}`);
        setLoading(false);
        return;
      }

      apiClient.get('/api/dashboard/risk')
        .then(res => {
          const servers = res.data.servers || [];
          const match = servers.find(s => s.id === id);
          if (match) {
            setAsset(prev => ({ ...prev, risk_score: match.risk_score }));
            setRiskData({
              risk_score: match.risk_score,
              contributing_factors: match.contributing_factors || {
                exposure: { score: 0, open_ports: 0 },
                criticality: { base_score: 0, level: 'MEDIUM' },
                vulnerabilities: { score: 0, total_vulns: 0 },
                scan_freshness: { score: 0, days_since_scan: 0 },
              },
            });
          } else {
            setRiskData({
              risk_score: 0,
              contributing_factors: {
                exposure: { score: 0, open_ports: 0 },
                criticality: { base_score: 0, level: 'MEDIUM' },
                vulnerabilities: { score: 0, total_vulns: 0 },
                scan_freshness: { score: 0, days_since_scan: 0 },
              },
            });
          }
        })
        .catch(err => {
          console.warn('[AssetDetail] Risk data fetch failed (Non-critical):', err.message);
          setRiskData({
            risk_score: 0,
            contributing_factors: {
              exposure: { score: 0, open_ports: 0 },
              criticality: { base_score: 0, level: 'MEDIUM' },
              vulnerabilities: { score: 0, total_vulns: 0 },
              scan_freshness: { score: 0, days_since_scan: 0 },
            },
          });
        });

      apiClient.get(`/api/changes?server_id=${id}`)
        .then(res => {
          const changes = res.data.changes || [];
          setRevisions(changes.map(c => ({
            id: c.id,
            change_type: c.change_type,
            created_at: c.detected_at,
            changed_by: 'System',
            field_changes: {
              package: { old: c.old_version, new: c.new_version },
            },
          })));
        })
        .catch(err => {
          console.warn('[AssetDetail] Changes fetch failed (Non-critical):', err.message);
        });

      Promise.allSettled([
        apiClient.get(`/api/alerts/ioc?server_id=${id}`),
        apiClient.get(`/api/alerts/cve?server_id=${id}`),
      ]).then(([iocRes, cveRes]) => {
        if (iocRes.status === 'fulfilled') {
          setIocAlerts(iocRes.value.data.ioc_alerts || []);
        }
        if (cveRes.status === 'fulfilled') {
          setCveAlerts(cveRes.value.data.cve_alerts || []);
        }
        setAlertsLoading(false);
      }).catch(() => setAlertsLoading(false));

      apiClient.get(`/api/drift/${id}`)
        .then(res => {
          const deviations = res.data.deviations || [];
          setDriftEvents(deviations.map((d, i) => ({
            id: i,
            drift_type: d.type,
            detected_at: res.data.baseline_at || new Date().toISOString(),
            description: `${d.package_name}: ${d.old_version || '(none)'} → ${d.new_version || '(none)'}`,
          })));
        })
        .catch(() => {
          // No baseline set — drift events stay empty
        });

    } catch (err) {
      setError('An unexpected system error occurred while loading asset details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssetData();
  }, [id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this asset? This cannot be undone.')) return;
    try {
      await apiClient.delete(`/api/servers/${id}`);
      navigate('/assets');
    } catch (err) {
      alert('Failed to delete asset: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return (
      <PageContainer title="Loading Asset Details...">
        <div className="flex flex-col items-center justify-center h-96 space-y-4">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
          <p className="text-slate-500 font-medium animate-pulse">Syncing with discovery engine...</p>
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Discovery Error">
        <div className="flex flex-col items-center justify-center py-20 bg-navy-800 rounded-3xl border border-red-500/20 shadow-sm">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Data Retrieval Failed</h2>
          <p className="text-gray-400 mb-8 max-w-md text-center">{error}</p>
          <div className="flex space-x-4">
            <Button variant="outline" onClick={() => navigate('/assets')}>Back to Inventory</Button>
            <Button onClick={fetchAssetData}>Retry Connection</Button>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!asset) {
    return (
      <PageContainer title="Not Found">
        <div className="flex flex-col items-center justify-center py-20 bg-navy-800 rounded-3xl border border-gray-700 shadow-sm">
          <div className="w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mb-6">
            <Info className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Asset Not Found</h2>
          <p className="text-gray-400 mb-8 max-w-md text-center">The requested asset record could not be located in the discovery database.</p>
          <Button variant="outline" onClick={() => navigate('/assets')}>Back to Inventory</Button>
        </div>
      </PageContainer>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid },
    { id: 'risk', label: 'Risk Analysis', icon: Shield },
    { id: 'ioc', label: `IoC Alerts${iocAlerts.length ? ` (${iocAlerts.length})` : ''}`, icon: AlertTriangle },
    { id: 'cve', label: `CVE Alerts${cveAlerts.length ? ` (${cveAlerts.length})` : ''}`, icon: ShieldAlert },
    { id: 'history', label: 'History', icon: History },
    { id: 'drift', label: 'Drift Events', icon: Activity },
    { id: 'services', label: 'Services & Ports', icon: Network },
  ];

  return (
    <PageContainer
      title={asset.name}
      subtitle={`First detected on ${new Date(asset.created_at).toLocaleDateString()}`}
      actions={
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={() => {
            setEditedName(asset.name);
            setIsEditingName(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Edit Name
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/assets')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button variant="outline" size="sm" className="text-red-400 hover:bg-red-500/10 hover:text-red-400 border-red-500/30" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
          <Button size="sm" onClick={fetchAssetData} className="bg-primary hover:bg-primary-dark">
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Data
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="col-span-1 space-y-6">
          <Card className="p-6 bg-navy-800 border-gray-700">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-20 h-20 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 border border-primary/20">
                <Server className="w-10 h-10 text-primary" />
              </div>
              <div className="flex items-center justify-center space-x-2 group">
                <h2 className="text-xl font-bold text-white">{asset.name}</h2>
                <button 
                  onClick={() => {
                    setEditedName(asset.name);
                    setIsEditingName(true);
                  }}
                  className="p-1 text-gray-400 hover:text-primary hover:bg-primary/5 rounded-md transition-all opacity-0 group-hover:opacity-100"
                  title="Rename Asset"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-sm text-gray-400 font-mono bg-navy-900 px-3 py-1 rounded-full mt-2 border border-gray-700">{asset.ip_address}</p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                <Badge variant={asset.is_baseline ? "success" : "secondary"}>{asset.is_baseline ? "Baseline" : "Non-Baseline"}</Badge>
                <Badge variant="outline" className="capitalize">{asset.asset_type || 'Server'}</Badge>
              </div>
            </div>
            <div className="space-y-4 border-t border-gray-700 pt-6">
              <InfoRow label="Hostname" value={asset.hostname || '-'} icon={Globe} />
              <InfoRow label="MAC Address" value={asset.mac_address || '-'} icon={Database} />
              <InfoRow label="OS Type" value={asset.os_type || '-'} icon={Cpu} />
              <InfoRow label="Criticality" value={asset.asset_criticality || 'MEDIUM'} icon={AlertTriangle} />
              <InfoRow label="Last Scanned" value={asset.last_scanned ? new Date(asset.last_scanned).toLocaleString() : 'Never'} icon={Clock} />
            </div>
          </Card>

          <Card className="p-6 bg-navy-800 text-white border-gray-700 shadow-xl overflow-hidden relative">
            <div className="relative z-10">
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-4 tracking-widest">Security Risk Surface</h3>
              <div className="flex items-end space-x-2">
                <span className="text-6xl font-black">{asset.risk_score || 0}</span>
                <span className="text-gray-400 font-bold mb-2">/ 100</span>
              </div>
              <div className="mt-6"><RiskBadge score={asset.risk_score} size="lg" /></div>
            </div>
            <div className="absolute -right-12 -bottom-12 opacity-5 rotate-12"><Shield className="w-56 h-56" /></div>
          </Card>
        </div>

        <div className="col-span-2 space-y-6">
          <div className="flex border-b border-gray-700 overflow-x-auto no-scrollbar bg-navy-800 rounded-t-xl px-2">
            {tabs.map(tab => (
              <button 
                key={tab.id} 
                onClick={() => setActiveTab(tab.id)} 
                className={`flex items-center space-x-2 px-6 py-4 text-sm font-bold transition-all border-b-2 -mb-[2px] whitespace-nowrap ${activeTab === tab.id ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-gray-400 hover:text-gray-200 hover:bg-navy-900'}`}
              >
                <tab.icon className="w-4 h-4" /><span>{tab.label}</span>
              </button>
            ))}
          </div>

          <div className="mt-2 min-h-[400px]">
            {activeTab === 'overview' && <OverviewTab asset={asset} />}
            {activeTab === 'risk' && <RiskTab riskData={riskData} />}
            {activeTab === 'ioc' && <IocAlertsTab alerts={iocAlerts} loading={alertsLoading} />}
            {activeTab === 'cve' && <CveAlertsTab alerts={cveAlerts} loading={alertsLoading} />}
            {activeTab === 'history' && <HistoryTab revisions={revisions} />}
            {activeTab === 'drift' && <DriftTab events={driftEvents} id={id} />}
            {activeTab === 'services' && <ServicesTab asset={asset} />}
          </div>
        </div>
      </div>

      {/* Edit Name Modal */}
      {isEditingName && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <Card className="w-full max-w-md p-6 animate-in zoom-in-95 duration-200 bg-navy-800 border-gray-700">
            <h3 className="text-xl font-bold text-white mb-4">Rename Asset</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-gray-400 mb-1.5 block">Asset Name</label>
                <input 
                  type="text" 
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full px-4 py-3 bg-navy-900 border border-gray-700 rounded-xl text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium"
                  placeholder="Enter custom asset name..."
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setIsEditingName(false)}>Cancel</Button>
                <Button className="flex-1" onClick={async () => {
                  try {
                    await apiClient.put(`/api/servers/${id}`, { hostname: editedName });
                    setAsset({ ...asset, name: editedName });
                    setIsEditingName(false);
                  } catch (err) {
                    alert('Failed to rename asset: ' + (err.response?.data?.error || err.message));
                  }
                }}>Save Changes</Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
  );
};

const InfoRow = ({ label, value, icon: Icon }) => (
  <div className="flex items-center justify-between py-1.5 hover:bg-navy-900 px-2 rounded transition-colors">
    <div className="flex items-center text-gray-400 text-sm"><Icon className="w-4 h-4 mr-3" /><span>{label}</span></div>
    <span className="text-sm font-bold text-white">{value}</span>
  </div>
);

const OverviewTab = ({ asset }) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-5 bg-navy-900 border-gray-700 shadow-inner">
        <h4 className="text-xs font-bold text-gray-400 uppercase mb-3 tracking-wider">Network Configuration</h4>
        <div className="space-y-2">
          <p className="text-sm text-gray-300 flex justify-between"><span>IP Address:</span> <span className="font-mono font-bold text-white">{asset.ip_address}</span></p>
          <p className="text-sm text-gray-300 flex justify-between"><span>Primary Subnet:</span> <span className="font-mono font-bold text-white">{asset.subnet || 'Auto-detected'}</span></p>
        </div>
      </Card>
      <Card className="p-5 bg-navy-900 border-gray-700 shadow-inner">
        <h4 className="text-xs font-bold text-gray-400 uppercase mb-3 tracking-wider">Asset Identification</h4>
        <div className="space-y-2">
          <p className="text-sm text-gray-300 flex justify-between"><span>MAC Address:</span> <span className="font-mono font-bold text-white">{asset.mac_address || 'N/A'}</span></p>
          <p className="text-sm text-gray-300 flex justify-between"><span>Serial Number:</span> <span className="font-mono font-bold text-white">{asset.serial_number || 'N/A'}</span></p>
        </div>
      </Card>
    </div>
    <Card className="p-6 border-gray-700 bg-navy-800">
      <h4 className="text-sm font-bold text-white mb-4 flex items-center"><Info className="w-4 h-4 mr-2 text-primary" /> Discovery Intelligence</h4>
      <div className="prose prose-invert prose-sm max-w-none text-gray-300">
        <p>This asset was first identified by the automated scanner on <strong>{new Date(asset.created_at).toLocaleString()}</strong>. It is currently categorized as a <strong>{asset.asset_type || 'managed server'}</strong> within the organization's infrastructure.</p>
        <p className="mt-2">Operating system fingerprints suggest <strong>{asset.os_type || 'Unknown OS'}</strong>, requiring further verification via authenticated scanning for detailed versioning.</p>
      </div>
    </Card>
  </div>
);

const IocAlertsTab = ({ alerts, loading }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-navy-800 rounded-xl border border-gray-700">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-gray-400 font-medium">Loading IoC alerts...</p>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="text-center py-20 bg-navy-800 rounded-3xl border border-dashed border-gray-700">
        <ShieldCheck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 font-bold">No IoC alerts for this asset.</p>
        <p className="text-gray-500 text-sm mt-1">No indicators of compromise detected via MISP enrichment.</p>
      </div>
    );
  }

  const THREAT_COLORS = {
    high: 'bg-red-400/10 text-red-400 border-red-400/20',
    medium: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
    low: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    undefined: 'bg-gray-400/10 text-gray-400 border-gray-400/20',
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {alerts.map(alert => (
        <Card key={alert.id} className="p-5 border-l-4 border-l-red-500 shadow-sm hover:shadow-lg transition-all">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-black text-white tracking-tight uppercase">{alert.ioc_type}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${THREAT_COLORS[alert.misp_threat_level] || THREAT_COLORS.undefined}`}>
                  {alert.misp_threat_level || 'undefined'}
                </span>
                {alert.resolved ? (
                  <Badge size="xs" variant="success">Resolved</Badge>
                ) : (
                  <Badge size="xs" variant="danger">Active</Badge>
                )}
              </div>
              <p className="text-sm text-gray-300 font-mono bg-gray-950 px-2 py-1 rounded">{alert.ioc_value}</p>
              {alert.description && <p className="text-sm text-gray-400">{alert.description}</p>}
              {alert.attack_technique && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-slate-900 text-amber-400">
                  {alert.attack_technique}
                </span>
              )}
              <p className="text-xs text-gray-500 font-medium flex items-center">
                <Clock className="w-3 h-3 mr-1" /> {new Date(alert.detected_at).toLocaleString()}
              </p>
            </div>
            {alert.misp_event_id && (
              <span className="text-xs text-gray-500 font-mono">MISP: {alert.misp_event_id}</span>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};

const CveAlertsTab = ({ alerts, loading }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-navy-800 rounded-xl border border-gray-700">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-gray-400 font-medium">Loading CVE alerts...</p>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="text-center py-20 bg-navy-800 rounded-3xl border border-dashed border-gray-700">
        <ShieldCheck className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 font-bold">No CVE alerts for this asset.</p>
        <p className="text-gray-500 text-sm mt-1">No known vulnerabilities detected in installed packages.</p>
      </div>
    );
  }

  const SEVERITY_COLORS = {
    CRITICAL: 'bg-red-400/10 text-red-400 border-red-400/20',
    HIGH: 'bg-orange-400/10 text-orange-400 border-orange-400/20',
    MEDIUM: 'bg-amber-400/10 text-amber-400 border-amber-400/20',
    LOW: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
  };

  return (
    <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {alerts.map(alert => (
        <Card key={alert.id} className="p-5 border-l-4 border-l-amber-500 shadow-sm hover:shadow-lg transition-all">
          <div className="flex justify-between items-start">
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-black text-slate-800 tracking-tight uppercase font-mono">{alert.cve_id}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-semibold border ${SEVERITY_COLORS[alert.severity] || SEVERITY_COLORS.LOW}`}>
                  {alert.severity}
                </span>
                {alert.resolved ? (
                  <Badge size="xs" variant="success">Resolved</Badge>
                ) : (
                  <Badge size="xs" variant="danger">Active</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <span className="font-mono bg-gray-950 px-2 py-0.5 rounded">{alert.package_name}</span>
                <span className="text-gray-500">@</span>
                <span className="font-mono bg-gray-950 px-2 py-0.5 rounded">{alert.version}</span>
                {alert.ecosystem && <span className="text-xs text-gray-500">({alert.ecosystem})</span>}
              </div>
              {alert.cvss_score && (
                <span className="text-xs font-mono text-gray-500">CVSS: {alert.cvss_score}</span>
              )}
              {alert.description && <p className="text-sm text-gray-400">{alert.description}</p>}
              <p className="text-xs text-gray-500 font-medium flex items-center">
                <Clock className="w-3 h-3 mr-1" /> {new Date(alert.detected_at).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const RiskTab = ({ riskData }) => {
  if (!riskData || !riskData.contributing_factors) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-navy-800 rounded-xl border border-gray-700">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-gray-400 font-medium">Calculating dynamic risk factors...</p>
      </div>
    );
  }

  const factors = [
    { id: 'exposure', name: 'Network Exposure', score: riskData.contributing_factors.exposure?.score || 0, desc: `${riskData.contributing_factors.exposure?.open_ports || 0} open ports detected` },
    { id: 'criticality', name: 'Asset Criticality', score: riskData.contributing_factors.criticality?.base_score || 0, desc: `Business impact weight: ${riskData.contributing_factors.criticality?.level || 'MEDIUM'}` },
    { id: 'vulns', name: 'Vulnerability Surface', score: riskData.contributing_factors.vulnerabilities?.score || 0, desc: `${riskData.contributing_factors.vulnerabilities?.total_vulns || 0} known CVEs across components` },
    { id: 'freshness', name: 'Scan Freshness', score: riskData.contributing_factors.scan_freshness?.score || 0, desc: `Last full scan was ${riskData.contributing_factors.scan_freshness?.days_since_scan || 0} days ago` },
  ];

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <h4 className="text-sm font-bold text-white mb-4 tracking-tight">Contributing Risk Factors</h4>
      <div className="grid grid-cols-1 gap-6">
        {factors.map(f => (
          <div key={f.id} className="p-4 bg-navy-800 border border-gray-700 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-center mb-3">
              <div>
                <span className="font-bold text-white">{f.name}</span>
                <p className="text-xs text-gray-400 mt-1">{f.desc}</p>
              </div>
              <div className="text-right">
                <span className={`text-lg font-black ${f.score > 70 ? 'text-red-500' : f.score > 40 ? 'text-amber-500' : 'text-emerald-500'}`}>{f.score}</span>
                <span className="text-gray-500 text-xs font-bold ml-1">/ 100</span>
              </div>
            </div>
            <div className="w-full bg-navy-900 h-2.5 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-1000 ease-out ${f.score > 70 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' : f.score > 40 ? 'bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]' : 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]'}`} 
                style={{ width: `${f.score}%` }} 
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AIAdvisoryTab = ({ asset, riskData }) => {
  const getRecommendations = () => {
    const recs = [];
    if ((asset.open_ports?.length || 0) > 5) recs.push("Highly exposed network surface. Consider closing unnecessary ports and implementing IP whitelisting.");
    if (asset.asset_criticality === 'CRITICAL' && !asset.is_baseline) recs.push("Critical asset lacks a verified Golden Baseline. Defining a baseline is essential for drift detection.");
    if (riskData?.contributing_factors?.scan_freshness?.days_since_scan > 7) recs.push("Discovery data is becoming stale. Schedule a comprehensive scan to refresh asset context.");
    if (asset.os_type === 'Unknown' || !asset.os_type) recs.push("OS Type unidentified. Run a 'Comprehensive Scan' to fingerprint the operating system and identify vulnerabilities.");
    
    if (recs.length === 0) recs.push("Asset security posture is optimal. Maintain current monitoring levels and verify backup integrity.");
    return recs;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="bg-primary/5 border border-primary/20 p-8 rounded-3xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-primary text-white rounded-2xl shadow-lg shadow-primary/30"><Lightbulb className="w-6 h-6" /></div>
            <div>
              <h4 className="text-lg font-bold text-white">Sentinel AI Advisor</h4>
              <p className="text-xs text-primary font-bold uppercase tracking-widest">Real-time Security Recommendations</p>
            </div>
          </div>
          <div className="space-y-4">
            {getRecommendations().map((rec, i) => (
              <div key={i} className="flex items-start p-4 bg-navy-800 border border-gray-700 rounded-2xl shadow-sm transform transition-transform hover:scale-[1.01]">
                <div className="w-6 h-6 bg-emerald-500/10 rounded-full flex items-center justify-center mr-4 mt-0.5 border border-emerald-500/20">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                </div>
                <p className="text-sm text-gray-200 leading-relaxed font-medium">{rec}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <ShieldCheck className="w-32 h-32 text-primary" />
        </div>
      </div>
      <div className="p-4 bg-navy-900 rounded-2xl border border-dashed border-gray-700">
        <p className="text-xs text-gray-400 text-center font-medium italic">AI recommendations are generated by the Project Sentinel engine using identified vulnerabilities, exposure surface, and drift history.</p>
      </div>
    </div>
  );
};

const HistoryTab = ({ revisions }) => (
  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
    {revisions.length > 0 ? (
      <div className="relative pl-10 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-gray-700">
        {revisions.map((rev, idx) => (
          <div key={rev.id} className="relative group">
            <div className={`absolute -left-[35px] top-0 w-8 h-8 rounded-full border-4 border-navy-800 flex items-center justify-center shadow-md transition-transform group-hover:scale-110 z-10 ${rev.change_type === 'create' ? 'bg-emerald-500' : 'bg-primary'}`}>
              {rev.change_type === 'create' ? <Plus className="w-4 h-4 text-white" /> : <Settings className="w-4 h-4 text-white" />}
            </div>
            <div className="bg-navy-800 p-4 rounded-xl border border-gray-700 shadow-sm group-hover:border-primary/30 transition-colors">
              <div className="flex justify-between items-center mb-1">
                <p className="text-sm font-black text-white uppercase tracking-tighter">{rev.change_type.replace('_', ' ')}</p>
                <Badge size="xs" variant="outline">{rev.changed_by || 'System'}</Badge>
              </div>
              <p className="text-xs text-gray-400 font-medium mb-3 flex items-center"><Clock className="w-3 h-3 mr-1" /> {new Date(rev.created_at).toLocaleString()}</p>
              {rev.field_changes && (
                <div className="mt-2 text-[11px] bg-navy-900 text-gray-300 p-3 rounded-lg font-mono leading-relaxed overflow-x-auto">
                  {Object.entries(rev.field_changes).map(([f, d]) => (
                    <div key={f} className="mb-1">
                      <span className="text-amber-400">{f}:</span> {JSON.stringify(d.old)} <span className="text-gray-500">→</span> <span className="text-emerald-400">{JSON.stringify(d.new)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="text-center py-20 bg-navy-800 rounded-3xl border border-dashed border-gray-700">
        <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 font-bold">No historical data available yet.</p>
      </div>
    )}
  </div>
);

const DriftTab = ({ events, id }) => (
  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
    {events.length > 0 ? events.map(e => (
      <Card key={e.id} className="p-5 border-l-4 border-l-red-500 shadow-sm hover:shadow-lg transition-all transform hover:-translate-y-0.5 bg-navy-800 border-gray-700">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-black text-white tracking-tight uppercase">{e.drift_type.replace('_', ' ')}</span>
              <Badge variant="danger" size="xs">Drift Detected</Badge>
            </div>
            <p className="text-xs text-gray-400 font-bold flex items-center"><Clock className="w-3 h-3 mr-1" /> {new Date(e.detected_at).toLocaleString()}</p>
            <p className="text-sm text-gray-300 font-medium leading-snug">{e.description || 'System state deviated from security baseline.'}</p>
          </div>
          <Button size="xs" variant="primary">Acknowledge</Button>
        </div>
      </Card>
    )) : (
      <div className="bg-navy-800 text-emerald-400 p-12 rounded-[2rem] border border-emerald-500/20 text-center shadow-inner relative overflow-hidden">
        <div className="relative z-10">
          <div className="w-20 h-20 bg-navy-900 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
            <ShieldCheck className="w-10 h-10 text-emerald-500" />
          </div>
          <h5 className="text-xl font-black text-white mb-2">Baseline Synchronized</h5>
          <p className="text-sm text-gray-400 font-medium max-w-xs mx-auto">This asset is currently in perfect compliance with its security baseline. No unauthorized drift detected.</p>
        </div>
        <div className="absolute -right-10 -bottom-10 p-10 opacity-5 pointer-events-none">
          <CheckCircle2 className="w-40 h-40 text-emerald-600" />
        </div>
      </div>
    )}
  </div>
);

const ServicesTab = ({ asset }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!asset?.id) return;
    setLoading(true);
    apiClient.get(`/api/servers/${asset.id}/snapshot`)
      .then(res => setSnapshot(res.data.snapshot))
      .catch(() => setSnapshot(null))
      .finally(() => setLoading(false));
  }, [asset?.id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-navy-800 rounded-xl border border-gray-700">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const ports = snapshot?.ports || [];
  const services = snapshot?.services || [];
  const osInfo = snapshot?.os_info;

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
      {osInfo && (
        <div className="p-4 bg-navy-900 border border-gray-700 rounded-xl">
          <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-1">OS Fingerprint</p>
          <p className="text-sm text-white font-mono">{osInfo}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h5 className="text-sm font-black text-white flex items-center mb-6 tracking-wider uppercase"><Network className="w-4 h-4 mr-3 text-primary" /> Network Exposure Surface</h5>
          <div className="flex flex-wrap gap-3">
            {ports.length > 0 ? ports.map((p, i) => (
              <span key={i} className="px-4 py-2 bg-navy-800 text-gray-200 rounded-xl text-sm font-mono font-bold border border-gray-700 shadow-sm hover:border-primary/30 transition-colors">
                {p.port}/{p.protocol}{p.service ? ` (${p.service})` : ''}
              </span>
            )) : (
              <div className="flex items-center space-x-2 text-gray-400 p-4 bg-navy-900 rounded-xl border border-dashed border-gray-700 w-full">
                <ShieldCheck className="w-4 h-4" />
                <span className="text-sm font-medium italic">No open ports detected. Stealth mode active.</span>
              </div>
            )}
          </div>
        </div>
        <div>
          <h5 className="text-sm font-black text-white flex items-center mb-6 tracking-wider uppercase"><Activity className="w-4 h-4 mr-3 text-primary" /> Active Service Fingerprints</h5>
          <div className="space-y-3">
            {services.length > 0 ? services.map((s, i) => (
              <div key={i} className="flex justify-between items-center p-4 bg-navy-800 rounded-2xl border border-gray-700 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-white tracking-tight">{typeof s === 'string' ? s : s.name || s.service || s}</span>
                </div>
              </div>
            )) : (
              <div className="flex items-center space-x-2 text-gray-400 p-4 bg-navy-900 rounded-xl border border-dashed border-gray-700 w-full">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm font-medium italic">No service fingerprints identified.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetDetail;